import { DateRangePicker } from 'app/ComponentLibrary/DateRangeNew';
import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useSelector } from 'react-redux';
import { selectIsEditMod, selectIsEmiLmoDisabled } from 'store/redux-store/placement-mods/selectors';
import { makeStyles } from 'tss-react/mui';
import { compareDates } from '../../ModificationsUtils';

const useStyles = makeStyles()(() => ({
  inputFields: {
    minWidth: '226px',
    height: '53px',
    '& .MuiFormHelperText-root': {
      width: 'max-content !important',
    },
  },
}));

export const EarlyMoveInDate = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { control, formState: { errors }, setValue, clearErrors } = useFormContext();
  const value: string | Date | null | undefined = useWatch({ name: 'earlyMoveInDate' });
  const moveInDate: string | Date | null | undefined = useWatch({ name: 'moveInDate' });
  const isModEdit = useSelector(selectIsEditMod);
  const { promiseInProgress: isSaveModApiCalling } = usePromiseTracker({ area: 'save-mod-api', delay: 0 });
  const isEmiLmoDisabled = useSelector(selectIsEmiLmoDisabled);

  const checkDateValidation = (emiDate: string | Date | null | undefined) => {
    const currentMoveInDate = moveInDate;
    if (currentMoveInDate && emiDate) {
      if (compareDates(emiDate, currentMoveInDate)) {
        setValue('emiNumberOfDays', '', { shouldDirty: true });
        return t('placement.placementMods.moveInDateError');
      }
    }

    setValue(
      'emiNumberOfDays',
      isNullOrUndefined(emiDate) || isEmpty(emiDate) ? '' : moment(moveInDate).diff(emiDate, 'days'),
      {
        shouldDirty: false,
      },
    );

    clearErrors('earlyMoveInDate');
    clearErrors('emiNumberOfDays');
    return null;
  };

  return (
    <Controller
      control={control}
      name={'earlyMoveInDate'}
      rules={{
        validate: checkDateValidation,
      }}
      render={({ field: { onChange, value, ...rest } }) => {
        const emiDate = moment(value);
        return (
          <React.Fragment>
            <DateRangePicker
              onChange={newValue => {
                if (newValue.startDate !== undefined) {
                  const newDate = moment(newValue.startDate);
                  const setTo = newDate.isValid() ? moment(newDate).format('YYYY-MM-DD') : null;
                  onChange(setTo);
                } else {
                  onChange(null);
                }
              }}
              value={value}
              open={false}
              quickSelect={false}
              variant="filled"
              fullWidth
              clear
              single
              placeholder="Early Move In Date"
              useMaxWidth={true}
              labelClass={classes.inputFields}
              trailingIconShow={true}
              className={classes.inputFields}
              actionBar={false}
              clickAway={true}
              readOnly={!isModEdit || isNullOrUndefined(moveInDate) || isEmpty(moveInDate) || isEmiLmoDisabled}
              keepClickAwayVal={true}
              isDisabled={isSaveModApiCalling}
              initialDateRange={{
                startDate: emiDate.isValid() ? new Date(emiDate.format('YYYY-MM-DDTHH:mm:ss')) : undefined,
                endDate: emiDate.isValid() ? new Date(emiDate.format('YYYY-MM-DDTHH:mm:ss')) : undefined,
                useLabelAsValue: true,
              }}
              error={!!errors?.earlyMoveInDate}
              helperText={!isNullOrUndefined(errors?.earlyMoveInDate) ? errors?.earlyMoveInDate?.message : undefined}
              {...rest}
            />
          </React.Fragment>
        );
      }}
    />
  );
};
